import { API, authenticationService } from '@enkla-gui'
import { useCallback, useEffect, useRef, useState } from 'react'

const dismissedSessionIds = new Set()
const SEEN_RETRY_DELAYS = [500, 2000]
// Default polling interval (5 minutes)
const REFRESH_INTERVAL = 1000 * 60 * 5

export function useAnnouncements({ enabled = true, refreshInterval = REFRESH_INTERVAL } = {}) {
  const [banners, setBanners] = useState([])
  const fetchingRef = useRef(false)
  const intervalRef = useRef(null)

  const mergeAnnouncements = useCallback(list => {
    setBanners(prev => {
      const map = new Map(prev.map(b => [b.data.id, b]))
      list.forEach(a => {
        if (dismissedSessionIds.has(a.id)) return
        map.set(a.id, map.has(a.id) ? { ...map.get(a.id), data: a } : { data: a, status: 'visible', seenRequested: false })
      })
      const keepIds = new Set(list.map(a => a.id))
      const ordered = list.map(a => map.get(a.id)).filter(Boolean)
      return ordered.filter(b => keepIds.has(b.data.id))
    })
  }, [])

  const fetchAnnouncements = useCallback(async () => {
    if (!enabled || fetchingRef.current) return
    if (!authenticationService.currentTokenValue) return
    fetchingRef.current = true
    try {
      const res = await API.get('api/me/announcements')
      if (Array.isArray(res)) mergeAnnouncements(res)
    } finally {
      fetchingRef.current = false
    }
  }, [enabled, mergeAnnouncements])

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      return
    }
    fetchAnnouncements()
    if (refreshInterval > 0) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = setInterval(fetchAnnouncements, refreshInterval)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [enabled, refreshInterval, fetchAnnouncements])

  // Subscribe to auth state changes to refetch on login
  useEffect(() => {
    const subscription = authenticationService.currentToken.subscribe(token => {
      if (token && enabled) {
        // User logged in, refetch announcements
        fetchAnnouncements()
      } else if (!token) {
        // User logged out, clear banners
        setBanners([])
      }
    })
    return () => subscription.unsubscribe()
  }, [enabled, fetchAnnouncements])

  const markSeen = useCallback(id => {
    setBanners(prev => prev.map(b => {
      if (b.data.id !== id) return b
      // Do not hide immediately; rely on server to stop sending one_time announcements after they are seen.
      return { ...b, seenRequested: true }
    }))
    let attempt = 0
    const attemptPost = () => {
      const p = API.post && API.post(`api/me/announcements/${id}/seen`, {})
      if (p && p.catch) {
        p.catch(() => {
          if (attempt < SEEN_RETRY_DELAYS.length) {
            setTimeout(attemptPost, SEEN_RETRY_DELAYS[attempt++])
          }
        })
      }
    }
    attemptPost()
  }, [])

  const dismiss = useCallback(id => {
    dismissedSessionIds.add(id)
    let removed
    setBanners(prev => {
      removed = prev.find(b => b.data.id === id)
      return prev.filter(b => b.data.id !== id)
    })
    const p = API.post && API.post(`api/me/announcements/${id}/dismiss`, {})
    if (p && p.catch) {
      p.catch(e => {
        const s = e?.status
        if (s === 404 || s === 410) return
        if (removed) {
          dismissedSessionIds.delete(id)
          setBanners(prev => [{ ...removed, status: 'visible', error: 'Kunde inte stänga' }, ...prev])
        }
      })
    }
  }, [])

  return { banners: banners.filter(b => b.status !== 'hidden'), markSeen, dismiss, refetch: fetchAnnouncements, hasAuth: !!authenticationService.currentTokenValue }
}
